/**
* Copyright Aquera Inc 2023
*
* This source code is licensed under the BSD-3-Clause license found in the
* LICENSE file in the root directory of this source tree.
*/

import {css} from 'lit-element';

/**
 * Select_2 CSS
 */
export const styles = css`
  :host {
    box-sizing: border-box;
  }

  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }

  [hidden] {
    display: none !important;
  }

  .form-control .form-control__label {
    display: none;
  }

  .form-control .form-control__help-text {
    display: none;
  }

  /* Label */
  .form-control--has-label .form-control__label {
    display: inline-block;
    color: #333333;
    margin-bottom: 0.75rem;
  }

  .form-control--has-label.form-control--small .form-control__label {
    font-size: 0.875rem;
  }

  .form-control--has-label.form-control--medium .form-control__label {
    font-size: 1rem;
  }

  .form-control--has-label.form-control--large .form-control__label {
    font-size: 1.25rem;
  }

  :host([required]) .form-control--has-label .form-control__label::after {
    content: "*";
    margin-inline-start: -2px;
    color: inherit;
  }

  /* Help text */
  .form-control--has-help-text .form-control__help-text {
    display: block;
    color: #666666;
    margin-top: 0.75rem;
  }

  .form-control--has-help-text.form-control--small .form-control__help-text {
    font-size: 0.75rem;
  }

  .form-control--has-help-text.form-control--medium .form-control__help-text {
    font-size: 0.875rem;
  }

  .form-control--has-help-text.form-control--large .form-control__help-text {
    font-size: 1rem;
  }

  .form-control--has-help-text.form-control--radio-group .form-control__help-text {
    margin-top: 0.25rem;
  }

  :host {
    display: block;
  }

  /** The popup */
  .select {
    flex: 1 1 auto;
    display: inline-flex;
    width: 100%;
    position: relative;
    vertical-align: middle;
  }

  .select::part(popup) {
    z-index: 900;
  }

  .select[data-current-placement^='top']::part(popup) {
    transform-origin: bottom;
  }

  .select[data-current-placement^='bottom']::part(popup) {
    transform-origin: top;
  }

  /* Combobox */
  .select__combobox {
    flex: 1;
    display: flex;
    width: 100%;
    min-width: 0;
    position: relative;
    align-items: center;
    justify-content: start;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 400;
    letter-spacing: normal;
    vertical-align: middle;
    overflow: hidden;
    cursor: pointer;
    transition: 150ms color, 150ms border, 150ms box-shadow, 150ms background-color;
  }

  .select__display-input {
    position: relative;
    width: 100%;
    font: inherit;
    border: none;
    background: none;
    color: #333333;
    cursor: inherit;
    overflow: hidden;
    padding: 0;
    margin: 0;
    -webkit-appearance: none;
  }

  .select:not(.select--disabled):hover .select__display-input {
    color: #333333;
  }

  .select__display-input:focus {
    outline: none;
  }

  /* Visually hide the display input when multiple is enabled */
  .select--multiple:not(.select--placeholder-visible) .select__display-input {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  .select__value-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    opacity: 0;
    z-index: -1;
  }

  .select__tags {
    display: flex;
    flex: 1;
    align-items: center;
    flex-wrap: wrap;
    margin-inline-start: 0.5rem;
  }

  .select__tags::slotted(nile-tag) {
    cursor: pointer !important;
  }

  .select--disabled .select__tags,
  .select--disabled .select__tags::slotted(nile-tag) {
    cursor: not-allowed !important;
  }

  /* Standard selects */
  .select--standard .select__combobox {
    background-color: #FFFFFF;
    border: solid 1px #CCCCCC;
  }

  .select--standard.select--disabled .select__combobox {
    background-color: #F4F4F4;
    border-color: #CCCCCC;
    color: #999999;
    opacity: 0.5;
    cursor: not-allowed;
    outline: none;
  }

  .select--standard:not(.select--disabled).select--open .select__combobox,
  .select--standard:not(.select--disabled).select--focused .select__combobox {
    background-color: #F4F4F4;
    border-color: #0059FF;
    box-shadow: 0 0 0 3px rgba(0, 89, 255, 0.4);
  }

  /* Filled selects */
  .select--filled .select__combobox {
    border: none;
    background-color: #F4F4F4;
    color: #333333;
  }

  .select--filled:hover:not(.select--disabled) .select__combobox {
    background-color: #F4F4F4;
  }

  .select--filled.select--disabled .select__combobox {
    background-color: #F4F4F4;
    opacity: 0.5;
    cursor: not-allowed;
  }

  .select--filled:not(.select--disabled).select--open .select__combobox,
  .select--filled:not(.select--disabled).select--focused .select__combobox {
    background-color: #F4F4F4;
    outline: 3px solid rgba(0, 89, 255, 0.4);
  }

  /* Sizes */
  .select--small .select__combobox {
    border-radius: 0.25rem;
    font-size: 0.875rem;
    min-height: 1.875rem;
    padding-block: 0;
    padding-inline: 0.5rem;
  }

  .select--small .select__clear {
    margin-inline-start: 0.5rem;
  }

  .select--small .select__prefix::slotted(*) {
    margin-inline-end: 0.5rem;
  }

  .select--small.select--multiple:not(.select--placeholder-visible) .select__combobox {
    padding-block: 2px;
    padding-inline-start: 0;
  }

  .select--small .select__tags {
    gap: 2px;
  }

  .select--medium .select__combobox {
    border-radius: 0.25rem;
    font-size: 1rem;
    min-height: 2.5rem;
    padding-block: 0;
    padding-inline: 0.75rem;
  }

  .select--medium .select__clear {
    margin-inline-start: 0.75rem;
  }

  .select--medium .select__prefix::slotted(*) {
    margin-inline-end: 0.75rem;
  }

  .select--medium.select--multiple:not(.select--placeholder-visible) .select__combobox {
    padding-inline-start: 0;
    padding-block: 3px;
  }

  .select--medium .select__tags {
    gap: 3px;
  }

  .select--large .select__combobox {
    border-radius: 0.5rem;
    font-size: 1.25rem;
    min-height: 3.125rem;
    padding-block: 0;
    padding-inline: 1rem;
  }

  .select--large .select__clear {
    margin-inline-start: 1rem;
  }

  .select--large .select__prefix::slotted(*) {
    margin-inline-end: 1rem;
  }

  .select--large.select--multiple:not(.select--placeholder-visible) .select__combobox {
    padding-inline-start: 0;
    padding-block: 4px;
  }

  .select--large .select__tags {
    gap: 4px;
  }

  /* Pills */
  .select--pill.select--small .select__combobox {
    border-radius: 1.875rem;
  }

  .select--pill.select--medium .select__combobox {
    border-radius: 2.5rem;
  }

  .select--pill.select--large .select__combobox {
    border-radius: 3.125rem;
  }

  /* Prefix */
  .select__prefix {
    flex: 0;
    display: inline-flex;
    align-items: center;
    color: #AAAAAA;
  }

  /* Clear button */
  .select__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    color: #AAAAAA;
    border: none;
    background: none;
    padding: 0;
    transition: 150ms color;
    cursor: pointer;
  }

  .select__clear:hover {
    color: #999999;
  }

  .select__clear:focus {
    outline: none;
  }

  /* Expand icon */
  .select__expand-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    transition: 250ms rotate ease;
    rotate: 0;
    margin-inline-start: 0.25rem;
  }

  .select--open .select__expand-icon {
    rotate: -180deg;
  }

  /* Listbox */
  .select__listbox {
    display: block;
    position: relative;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-size: 1rem;
    font-weight: 400;
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
    background: #FFFFFF;
    border: solid 1px #CCCCCC;
    border-radius: 0.25rem;
    padding-block: 0.25rem;
    padding-inline: 0;
    overflow: auto;
    overscroll-behavior: none;

    /* Make sure it adheres to the popup's auto size */
    max-width: auto;
    max-height: auto;
  }

  .select__listbox::slotted(nile-divider) {
    --spacing: 0.25rem;
  }

  .select__listbox::slotted(small) {
    font-size: 0.875rem;
    font-weight: 600;
    color: #888888;
    padding-block: 0.25rem;
    padding-inline: 1rem;
  }
`;

export default [styles];
